import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(email, password);
        if (u.must_change_password) nav("/trocar-senha");
        else nav(u.role === "superadmin" ? "/admin" : "/painel");
      } else {
        await api.post("/auth/forgot-password", { email });
        toast.success("Se o e-mail existir, enviaremos uma senha temporária.");
        setMode("login");
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#08070A] flex flex-col items-center justify-center px-5 py-10 relative overflow-hidden">
      {/* abstract ambient glow */}
      <div className="pointer-events-none absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full blur-[90px] opacity-40" style={{ background: "radial-gradient(circle,#FF8A00,transparent 65%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -right-20 h-[460px] w-[460px] rounded-full blur-[100px] opacity-40" style={{ background: "radial-gradient(circle,#FF2D8E,transparent 65%)" }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 0.5px, transparent 0.5px)", backgroundSize: "26px 26px" }} />

      <div className="relative z-10 w-full max-w-sm flex flex-col items-center">
        <Link to="/"><img src="/hmmm-logo.png" alt="HMMM MENU" className="h-24 sm:h-28 w-auto drop-shadow-[0_8px_40px_rgba(255,74,110,0.35)]" /></Link>
        <p className="text-[11px] sm:text-xs tracking-[0.35em] text-zinc-400 mt-3 text-center font-medium">SEU CARDÁPIO,<br className="sm:hidden" /> MAIS SABOR PARA TODOS.</p>

        <div className="mt-8 w-full hmmm-gradient rounded-[26px] p-[1.5px] hmmm-glow">
          <form onSubmit={submit} className="rounded-[25px] bg-[#0E0D11]/95 backdrop-blur-xl p-7" data-testid="login-form">
            <h1 className="font-head text-3xl font-extrabold">{mode === "login" ? "Entrar" : "Recuperar acesso"}</h1>
            <p className="text-sm text-zinc-500 mt-1 mb-6">{mode === "login" ? "Acesse o painel do seu restaurante." : "Enviaremos uma senha temporária para seu e-mail."}</p>

            <label className="text-xs font-semibold text-zinc-400">E-mail</label>
            <div className="mt-1.5 mb-4 flex items-center gap-2 rounded-2xl bg-white/[0.04] border border-white/10 px-4 focus-within:border-[#FF5A5F] transition-colors">
              <Mail size={18} className="text-zinc-500" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="login-email" placeholder="voce@restaurante.com" className="flex-1 bg-transparent py-3.5 outline-none text-sm" />
            </div>

            {mode === "login" && (
              <>
                <label className="text-xs font-semibold text-zinc-400">Senha</label>
                <div className="mt-1.5 mb-2 flex items-center gap-2 rounded-2xl bg-white/[0.04] border border-white/10 px-4 focus-within:border-[#FF5A5F] transition-colors">
                  <Lock size={18} className="text-zinc-500" />
                  <input value={password} onChange={(e) => setPassword(e.target.value)} type={show ? "text" : "password"} required data-testid="login-password" placeholder="••••••••" className="flex-1 bg-transparent py-3.5 outline-none text-sm" />
                  <button type="button" onClick={() => setShow(!show)} data-testid="login-toggle-password" className="text-zinc-500 hover:text-white">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
                <button type="button" onClick={() => setMode("forgot")} className="text-xs hmmm-text font-bold mb-5" data-testid="forgot-link">Esqueci minha senha</button>
              </>
            )}

            <button disabled={loading} data-testid="login-submit" className="w-full rounded-full hmmm-gradient py-3.5 font-bold text-white hmmm-glow disabled:opacity-60 active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
              {loading ? "Aguarde..." : mode === "login" ? <>Entrar <ArrowRight size={18} /></> : "Enviar"}
            </button>
            {mode === "forgot" && <button type="button" onClick={() => setMode("login")} className="w-full text-center text-xs text-zinc-400 mt-4">Voltar para o login</button>}
          </form>
        </div>
      </div>
    </div>
  );
}
